import { Profiler, type PropsWithChildren } from 'react';
import {
  LOCAL_WIDGET_ID,
  Show,
  WidgetTokenProvider
} from '@elfsight/lib-utils';
import { WidgetI18nProvider } from '@elfsight/widget-i18n';
import { useCustomJS, useDefineAPISchema } from '@elfsight/widget-js-api';
import { PortalNestProvider } from '@elfsight/widget-modal';
import {
  CustomCSS,
  createCustomCSSPrefixClassName,
  EmbedRoot,
  ShadowEmbedRoot,
  WidgetFontsLoader
} from '@elfsight/widget-ui';
import { useSettings } from '@wu/widget-core';
import DICTIONARY from '../../language/dictionary.json';
import { meta } from '../constants';
import { DataProvider, PreparedSourceProvider } from './data';
import { LayoutProvider } from './layout';
import { ErrorBoundary, Main } from './main';
import { PopupProvider } from './popup';
import { VideoAutoplayProvider } from './post';
import { TabsProvider } from './tabs';
import { useSocialFeedSettings } from './utils';

const handleProfileRender = () => {};

const { alias } = meta;

function WidgetProviders({ children }: PropsWithChildren) {
  return (
    <TabsProvider>
      <DataProvider>
        <VideoAutoplayProvider>
          <LayoutProvider>
            <PopupProvider>{children}</PopupProvider>
          </LayoutProvider>
        </VideoAutoplayProvider>
      </DataProvider>
    </TabsProvider>
  );
}

export function Widget() {
  const {
    editMode,
    renderThumbnailMode,
    widgetId = '1',
    widgetWidth,
    widgetToken,
    customJS,
    customCSS,
    _shadowDOMDisabled
  } = useSettings();
  const { font, language } = useSocialFeedSettings();

  const customCSSPrefixClassName = createCustomCSSPrefixClassName(
    alias,
    widgetId
  );

  useDefineAPISchema();
  useCustomJS(customJS, editMode || renderThumbnailMode);

  return (
    <WidgetTokenProvider
      widgetId={widgetId || LOCAL_WIDGET_ID}
      widgetToken={widgetToken}
    >
      <Profiler id="widget" onRender={handleProfileRender}>
        <ErrorBoundary>
          <WidgetI18nProvider
            dictionary={DICTIONARY}
            language={language?.language || 'en'}
            override={language?.messages ?? {}}
          >
            <WidgetFontsLoader fonts={[font]} />

            <PreparedSourceProvider>
              <Show when={!_shadowDOMDisabled}>
                <ShadowEmbedRoot
                  width={widgetWidth}
                  font={font}
                  customCSS={customCSS}
                  customCSSClassName={customCSSPrefixClassName}
                >
                  <PortalNestProvider className={customCSSPrefixClassName}>
                    <WidgetProviders>
                      <Main />
                    </WidgetProviders>
                  </PortalNestProvider>
                </ShadowEmbedRoot>
              </Show>
              <Show when={_shadowDOMDisabled === true}>
                <PortalNestProvider
                  customCSSClassName={customCSSPrefixClassName}
                >
                  <WidgetProviders>
                    <EmbedRoot
                      id={`eapps-${alias}-${widgetId}`}
                      data-app={`eapps-${alias}`}
                      customCSSClassName={customCSSPrefixClassName}
                      width={widgetWidth}
                      className="RootLayout__RootComponent-sc"
                    >
                      <Main />

                      <CustomCSS
                        customCSS={customCSS}
                        prefixClassName={customCSSPrefixClassName}
                      />
                    </EmbedRoot>
                  </WidgetProviders>
                </PortalNestProvider>
              </Show>
            </PreparedSourceProvider>
          </WidgetI18nProvider>
        </ErrorBoundary>
      </Profiler>
    </WidgetTokenProvider>
  );
}
